/**
 * preview_screenshot — client-executed handler (Design note I step 4).
 *
 * Self-registering side-effect module (import once from agentTools.ts). The
 * WORKER tool (features/agent/tools/preview-screenshot.ts) stays the SOLE
 * advertiser + skill-gate authority (requiredSkills: ['build-app']); this entry
 * only owns the browser-side EXECUTION + the AG-UI result channel — the same
 * split db_editor uses.
 *
 * The handler runs against the args the WORKER forwarded (hooks.ts `runClientTool`
 * → `se.args`, since this tool registers `argsFrom:'worker'` for #356 — see the
 * registration note below). The worker forwards the model's own tool-call args
 * verbatim, so the resolved values match the model's call either way; path
 * resolution, live-vs-static selection, and rasterization all happen here. The
 * screenshot is NOT saved to the drive (#639 Task 4.3) — it lives only in
 * session-private CAS via the upload step below.
 *
 * Flow:
 *   1. Resolve the target file (model `path`, else the active preview tab's
 *      `data-file-id`, else give up with an actionable message).
 *   2. If a preview iframe for that file is open → request `papan:snapshot`
 *      (full post-JavaScript DOM) and rasterize it → mode 'live-snapshot'.
 *   3. Else fetch the file source and rasterize static markup → 'static-capture'.
 *   4. Upload the bytes through the DIRECT binary channel (#639 Task 4.2,
 *      spec §4.4) and return { handle, mime, mode, width, height } —
 *      NOT the raw base64. AG-UI caps a client tool RESULT at 512 KB
 *      (agui.ts:143); a real screenshot's base64 routinely exceeds that, so
 *      the image can never ride back inline as the tool result. The worker
 *      minted a dispatch-time one-use capability (Task 4.1,
 *      `awaitClientTool` in apps/worker/src/processors/agent.ts) and
 *      embedded it in the forwarded args as `__snapshotCapability` — this
 *      handler presents it to `POST …/attachments/snapshot`
 *      (`uploadSnapshotAttachment`), which consumes it (GETDEL, one-use) and
 *      commits the bytes into session-private CAS the same way a user
 *      upload does.
 *
 * Failures are reported to the file's debug log via reportExportError (src
 * 'export') with a distinguishable stage, and returned as { success:false }.
 */
import { filesApi } from '@papan/files/client'
import { rasterizeHtmlToImage, reportExportError } from '@papan/collab/client/preview-capture'
import { registerAgentTool, type AgentToolResult, type AgentToolHandlerCtx } from './registerAgentTool.js'
import { resolveReadyPreviewFrame, findActivePreviewFileId, toAbsoluteVfsPath, isPreviewReloading, previewStillMounting, previewCallCanResolve } from './previewIframe.js'
import { uploadSnapshotAttachment } from './sessionAttachmentsApi.js'
import { readClientToolTarget } from '../core/client-tool-target.js'
import { frameMessageTarget, frameMessageFrom } from '@papan/collab/ui/app-origin-frame'

interface ScreenshotArgs {
  path?: string
  width?: number
  fullPage?: boolean
  format?: 'png' | 'jpeg'
  /** #639 Task 4.1: the dispatch-time one-use capability the worker embeds
   *  into the forwarded args (NOT part of the model's own tool-call input —
   *  `awaitClientTool` adds it before publishing the client-tool-request).
   *  Carries only what this handler can't otherwise derive: `sessionId` and
   *  `toolCallId` already arrive via the handler's own `ctx` argument. */
  __snapshotCapability?: { runId: string; workspaceId: string }
}

const SNAPSHOT_TIMEOUT_MS = 15_000

/** Ask an open preview iframe to serialize its live, post-JavaScript DOM via the
 *  injected `papan:snapshot` bridge (same protocol as HtmlViewer.requestSnapshot).
 *  Returns the serialized HTML, or null when no preview answered in time. */
async function requestPreviewSnapshot(fileId: string, workspaceId: string | null): Promise<{ html: string; consoleSess: string } | null> {
  // Resolve a buffer whose bridge is confirmed live (pings across the
  // double-buffer reload swap) rather than posting at whichever iframe the DOM
  // calls visible mid-swap — which was silently degrading a live app to a
  // script-stripped static capture whenever a reload was in flight.
  const { frame } = await resolveReadyPreviewFrame([fileId], { workspaceId })
  const cw = frame?.contentWindow
  if (!cw) return null
  // #1408 R6: an app-origin frame is asked at, and heard from, its exact origin only.
  const target = frameMessageTarget(frame)
  return new Promise<{ html: string; consoleSess: string } | null>((resolve) => {
    const reqId = `agentsnap_${Date.now()}_${Math.random().toString(36).slice(2)}`
    const onMsg = (e: MessageEvent) => {
      // Source-gate: only the frame we asked may answer (reqId alone is guessable).
      if (!frameMessageFrom(e, cw, target)) return
      const d = e.data
      if (d && d.type === 'papan:snapshot:result' && d.reqId === reqId) {
        cleanup()
        resolve(
          d.error || typeof d.html !== 'string'
            ? null
            : { html: d.html, consoleSess: typeof d.consoleSess === 'string' ? d.consoleSess : '' },
        )
      }
    }
    const timer = setTimeout(() => { cleanup(); resolve(null) }, SNAPSHOT_TIMEOUT_MS)
    function cleanup() {
      clearTimeout(timer)
      window.removeEventListener('message', onMsg)
    }
    window.addEventListener('message', onMsg)
    try {
      cw.postMessage({ type: 'papan:snapshot', reqId }, target)
    } catch {
      cleanup()
      resolve(null)
    }
  })
}

function base64ToBlob(base64: string, mime: string): Blob {
  const bin = atob(base64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

/** Resolve the entry HTML file id for a `.miniapp` (or plain) folder: prefer
 *  `index.html`, else the first `.html` at the folder root. */
async function resolveFolderEntryHtml(folderId: string): Promise<string | null> {
  const index = await filesApi.resolvePath('index.html', folderId).catch(() => null)
  if (index?.id) return index.id
  const listing = await filesApi.listChildren(folderId).catch(() => null)
  const html = listing?.files.find((f) => !f.isFolder && f.name.toLowerCase().endsWith('.html'))
  return html?.id ?? null
}

async function handler(rawArgs: Record<string, unknown>, ctx: AgentToolHandlerCtx): Promise<AgentToolResult> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { success: false, error: 'No browser context for preview_screenshot.' }
  }
  const args = rawArgs as ScreenshotArgs
  const width = Math.min(3840, Math.max(320, Math.round(Number(args.width) || 1280)))
  const fullPage = !!args.fullPage
  const format: 'png' | 'jpeg' = args.format === 'jpeg' ? 'jpeg' : 'png'

  // ── 1. Resolve the target ids (+ the app folder for rasterizer context). ──
  // The live-preview iframe (tab-viewer) is keyed by tab.fileId — a standalone
  // HTML file's id, or a `.miniapp` FOLDER id (NOT its entry HTML). So look the
  // iframe up by the resolved path's OWN id (`previewLookupId`); the static
  // fallback uses the ENTRY HTML (`fileId`). Resolving straight to entry-HTML
  // for the iframe lookup made a `.miniapp` folder path silently MISS its live
  // preview and degrade to a script-stripped static capture.
  let previewLookupId: string | null = null
  let fileId: string | null = null
  if (args.path && args.path.trim()) {
    const resolved = await filesApi.resolvePath(toAbsoluteVfsPath(args.path)).catch(() => null)
    if (!resolved?.id) {
      return {
        success: false,
        error: `No file or folder at "${args.path}". Pass the app's HTML file (or its .pdapp folder), or omit path to use the open preview.`,
      }
    }
    previewLookupId = resolved.id
    if (resolved.file?.isFolder) {
      fileId = await resolveFolderEntryHtml(resolved.id)
      if (!fileId) {
        return {
          success: false,
          error: `"${args.path}" is a folder with no entry HTML (index.html). Point path at the app's HTML file instead.`,
        }
      }
    } else {
      fileId = resolved.id
    }
  } else {
    previewLookupId = findActivePreviewFileId(readClientToolTarget(rawArgs))
    if (!previewLookupId) {
      return {
        success: false,
        error:
          'No app preview is open and no path was given. Ask the user to open the app preview (or pass path = the HTML file / .pdapp folder), then try again.',
      }
    }
    fileId = previewLookupId
  }

  // App folder = the file's parent (standalone HTML) or the folder itself.
  // Passed to the rasterizer as `parentId` (asset/base-URL resolution
  // context) — not used for any drive write (#639 Task 4.3: the _debug/
  // save is gone).
  let appFolderId: string | null = null
  try {
    const info = await filesApi.getFileInfo(fileId)
    appFolderId = info.parentId
  } catch {
    /* best-effort — rasterization still proceeds without a parentId */
  }

  // ── 2. Live snapshot if a preview is open, else 3. static capture. ──
  const snapshot = await requestPreviewSnapshot(previewLookupId, readClientToolTarget(rawArgs)?.workspaceId ?? null)
  let html: string
  let mode: 'live-snapshot' | 'static-capture'
  // #385: only a LIVE snapshot carries a load session id (a static capture never
  // ran the app, so it has no captured runtime errors to report).
  let consoleSess = ''
  if (snapshot) {
    html = snapshot.html
    consoleSess = snapshot.consoleSess
    mode = 'live-snapshot'
  } else {
    try {
      const blob = await filesApi.getFileContent(fileId)
      html = await blob.text()
      mode = 'static-capture'
    } catch (e) {
      reportExportError('capture', e, { fileId })
      return {
        success: false,
        error: 'Could not load the app to capture. Open the app preview and try again.',
      }
    }
  }

  // ── rasterize ──
  let image
  try {
    image = await rasterizeHtmlToImage(html, {
      width,
      fullPage,
      format,
      parentId: appFolderId,
      fileId,
      snapshotFallback: mode === 'static-capture',
    })
  } catch (e) {
    reportExportError('rasterize', e, { fileId, dims: { captureWidth: width } })
    return { success: false, error: `Screenshot capture failed: ${e instanceof Error ? e.message : String(e)}` }
  }

  // ── 4. Upload through the direct binary channel (#639 Task 4.2). ──
  // The bytes cannot ride back as the AG-UI tool result (512 KB cap,
  // agui.ts:143) — present the dispatch-time capability the worker embedded
  // in `args.__snapshotCapability` and get back a small handle instead.
  const capability = args.__snapshotCapability
  if (!capability || typeof capability.runId !== 'string' || !capability.workspaceId) {
    reportExportError('deliver', new Error('Missing snapshot capability'), { fileId, level: 'warn' })
    return {
      success: false,
      error: 'No upload capability was provided for this snapshot — ask the agent to try again.',
    }
  }
  let handle: string
  try {
    const uploaded = await uploadSnapshotAttachment(
      ctx.sessionId,
      capability.workspaceId,
      base64ToBlob(image.base64, image.mime),
      { runId: capability.runId, toolCallId: ctx.toolCallId, filename: `screenshot.${format === 'jpeg' ? 'jpg' : 'png'}` },
    )
    handle = uploaded.handle
  } catch (e) {
    reportExportError('deliver', e, { fileId })
    return {
      success: false,
      error: `Could not upload the screenshot: ${e instanceof Error ? e.message : String(e)}`,
    }
  }

  return {
    success: true,
    data: {
      handle,
      mime: image.mime,
      mode,
      width: image.width,
      height: image.height,
      consoleSess,
    },
  }
}

registerAgentTool('preview_screenshot', {
  transport: 'client-executed',
  // #356: see previewEvalClient — route the live dispatch through
  // client-tool-request so ready() is consulted, and DEFER (retry via /pending)
  // while a preview reload is in flight instead of fast-failing mid-swap.
  argsFrom: 'worker',
  ready: (args) => !isPreviewReloading() && !previewStillMounting(readClientToolTarget(args)),
  // Some page/app tabs never get a preview iframe until the user acts (an
  // uninstalled PDApp, Settings face, consent prompt, over-ceiling page): keep
  // R5's reload-remount defer but answer after 20 s instead of hanging.
  maxDeferMs: 20_000,
  // #352 I1: only a tab with the preview the handler would use claims the call first.
  canResolve: previewCallCanResolve,
  handler,
})

// Exported for unit tests (the handler is otherwise only reachable via the
// registry side-effect above).
export { handler as __previewScreenshotHandler }
